'use client';

/**
 * Lets a page tell the app banner (rendered by the root layout, above the
 * page) what it is about, so the banner can say "Save Gillards Beach to a
 * trip" instead of "Download our app".
 *
 * The banner renders a slug-derived name with the server HTML; the page's
 * exact name replaces it after hydration. Same single truncated line either
 * way, so the swap never moves anything.
 */
import { useEffect, useSyncExternalStore } from 'react';

let current: string | null = null;
const listeners = new Set<() => void>();

function setName(name: string | null) {
  current = name;
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Read the page-supplied name (null until a page sets one). */
export function useAppBannerName(): string | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

/** Render on a page to name it in the app banner. Renders nothing. */
export default function AppBannerName({ name }: { name: string }) {
  useEffect(() => {
    setName(name);
    return () => setName(null);
  }, [name]);
  return null;
}
